<script setup lang="ts">
    import { usePriceHandler } from './composable/usePriceHandler';
    import { useEventStore } from '@/stores/event';
    import { useDisplay, useTheme } from 'vuetify';
    import { useEventCore } from '@/pages/core/event/composable/useEventCore';
    import { BetSelection, EventFeedType, EventPeriod } from '@/constants';
    import { useBookmakerDictionary } from '@/stores';
    import { formatOdds } from '@/helpers';
    import { oddsTooltip } from '@/helpers/event';
    import { usePlatform } from '@/composables/usePlatform';

    const {
        periodId = EventPeriod.FullTime,
        isShowAllMarketsToggle = false,
        isAllExpanded = false,
        isShow = false,
    } = defineProps<{
        periodId?: number;
        isShowAllMarketsToggle?: boolean;
        isAllExpanded?: boolean;
        isShow?: boolean;
    }>();

    const eventStore = useEventStore();
    const theme = useTheme();
    const display = useDisplay();
    const { onPriceClick } = usePriceHandler();
    const bookmakerDictionary = useBookmakerDictionary();
    const platform = usePlatform();

    const { isExpanded, isExpandedToggle } = useEventCore();

    const market = computed(() => {
        return periodId === EventPeriod.FirstHalf ? eventStore.marketBts1H! : eventStore.marketBtsFT!;
    });

    const { t } = useI18n();

    onMounted(() => {
        console.log('Component %s mounted.', 'app-eventMarketBts');
    });

    const emit = defineEmits(['toggle-all-markets']);
    const toggleAllMarkets = () => emit('toggle-all-markets');

    const isShowComp = computed(() => isShow);

    defineExpose({
        isShow: isShowComp,
        name: 'app-eventMarketBTS',
        periodId: periodId,
        isExpanded,
    });
</script>

<template>
    <v-card
        v-if="market !== null"
        tile
        elevation="0"
        style="padding-bottom: 1px"
        :style="eventStore.eventFeedType === EventFeedType.Live ? (theme.name.value === 'dark' ? 'background-color: #1e1f22' : 'background-color: #fff9f3') : ''"
    >
        <v-card-title class="font13 pa-0 market-header d-flex align-center" :class="eventStore.eventFeedType === EventFeedType.Live ? 'live' : 'pregame'">
            <v-avatar
                v-if="platform.isAggregator()"
                :data-tooltip="{ text: `${market.feedCount} ${t('event.bookmakers', market.feedCount ?? 0)}`, location: 'top' }"
                size="22"
                class="ml-2"
                :color="theme.name.value === 'dark' ? '#2b2c30' : eventStore.eventFeedType === EventFeedType.Live ? '#bd6100' : '#c5c5c5'"
            >
                <span class="whiteText font12 bold">
                    {{ market.feedCount }}
                </span>
            </v-avatar>
            <span :class="theme.name.value === 'dark' ? 'whiteText' : 'blackText'" class="pl-2 font-weight-regular">{{ t('event.BTTS') }}</span>
            <span v-if="periodId === EventPeriod.FirstHalf" :class="theme.name.value === 'dark' ? 'whiteText' : 'blackText'" class="pl-1 font-weight-regular">
                {{ t('event.market1H') }}
            </span>
            <v-spacer></v-spacer>
            <v-btn tag="a" v-if="isShowAllMarketsToggle" width="105" size="small" variant="outlined" color="tbGreen" class="mr-2 toggle-button font12" @click="toggleAllMarkets">
                <span v-if="isAllExpanded">{{ t('event.hideAll') }}</span>
                <span v-else>{{ t('event.showAll') }}</span>
            </v-btn>
            <v-icon v-if="isExpanded" class="mr-2" @click="isExpandedToggle()" :icon="mdiChevronUp" />
            <v-icon v-else class="mr-2" @click="isExpandedToggle()" :icon="mdiChevronDown" />
        </v-card-title>
        <v-card-text v-if="isExpanded" class="py-2">
            <v-row :class="display.width.value <= 430 ? 'px-0' : ''" class="py-1 my-0">
                <v-col :class="display.width.value <= 580 ? 'px-0' : ''" class="text-center py-1 d-flex flex-column align-center justify-center min-w">
                    <span v-if="platform.isAggregator()" class="bold grey--text" :class="display.width.value <= 430 ? 'font12' : 'font13'">
                        {{ market.feedCount }}
                        {{ t('event.feeds', market.feedCount ?? 0) }}
                    </span>
                </v-col>
                <v-col :class="display.width.value <= 580 ? 'px-1' : ''" class="text-center py-1" cols="4">
                    <div
                        v-if="market.yesPrice"
                        class="cursorPointer"
                        :class="[eventStore.eventFeedType === EventFeedType.Live ? 'live' : 'pregame', platform.isAggregator() ? 'odds-buttons' : 'odds-buttons-tbn']"
                        @click.stop="onPriceClick(market, periodId, BetSelection.BtsYes)"
                    >
                        <div :style="theme.name.value === 'dark' ? 'color: #f0f0f0' : ''" class="font13" :class="platform.isBookmaker() ? 'price-title-tbn' : 'price-title-tb'" style="height: 18px">
                            {{ t('event.yes') }}
                        </div>
                        <div v-xtooltip="platform.isAggregator() ? () => oddsTooltip(market.yesPriceList!, true, 0, 2) : null" :style="platform.isAggregator() ? 'height: 36px' : ''">
                            <div style="height: 18px">
                                <span :class="market.getHighlightClass(BetSelection.BtsYes, market.yesPrice)" class="odds nowrap font13">
                                    {{ formatOdds(market.yesPrice) }}
                                </span>
                            </div>

                            <div v-if="platform.isAggregator()" :class="['bookmaker' + market.yesBookmakerId, 'font12']" style="height: 18px">
                                {{ bookmakerDictionary.nameById(market.yesBookmakerId!) }}
                            </div>
                        </div>
                    </div>
                </v-col>
                <v-col :class="display.width.value <= 580 ? 'px-1' : ''" class="text-center py-1" cols="1"></v-col>
                <v-col :class="display.width.value <= 580 ? 'px-1' : ''" class="text-center py-1" cols="4">
                    <div
                        v-if="market.noPrice"
                        class="mr-1 cursorPointer"
                        :class="[eventStore.eventFeedType === EventFeedType.Live ? 'live' : 'pregame', platform.isAggregator() ? 'odds-buttons' : 'odds-buttons-tbn']"
                        @click.stop="onPriceClick(market, periodId, BetSelection.BtsNo)"
                    >
                        <div :style="theme.name.value === 'dark' ? 'color: #f0f0f0' : ''" class="font13" :class="platform.isBookmaker() ? 'price-title-tbn' : 'price-title-tb'" style="height: 18px">
                            {{ t('event.no') }}
                        </div>
                        <div v-xtooltip="platform.isAggregator() ? () => oddsTooltip(market.noPriceList!, true, 0, 2) : null" :style="platform.isAggregator() ? 'height: 36px' : ''">
                            <div style="height: 18px">
                                <span :class="market.getHighlightClass(BetSelection.BtsNo, market.noPrice)" class="odds nowrap font13">
                                    {{ formatOdds(market.noPrice) }}
                                </span>
                            </div>

                            <div v-if="platform.isAggregator()" :class="['bookmaker' + market.noBookmakerId, 'font12']" style="height: 18px">
                                {{ bookmakerDictionary.nameById(market.noBookmakerId!) }}
                            </div>
                        </div>
                    </div>
                </v-col>
                <v-col :class="display.width.value <= 580 ? 'px-0' : ''" class="text-center py-1 d-flex flex-column align-center justify-center min-w">
                    <span v-if="market.marginPerc" class="font13" :class="[Number(market.marginPerc) > 100 ? 'greyText' : 'bold greenText']">{{ market.marginPerc }}%</span>
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>
</template>

<style scoped>
    @import url('./styleMarketsCore.css');
</style>
